<template>
  <div class="employee-management attendance-calendar-page">
    <div class="lvyun-profile-banner">
      <h2>出勤日历</h2>
      <p>先选择员工，再按月查看和维护每日出勤状态（正常、请假、缺勤、加班）；支持 Excel 导入导出。</p>
    </div>

    <el-card class="employee-card" shadow="never">
      <template slot="header">
        <div class="card-header card-header--wrap">
          <div class="card-title">员工出勤日历</div>
          <div class="toolbar-actions">
            <el-select
              v-model="selectedEmployeeId"
              class="filter-employee"
              placeholder="请输入姓名/手机号查找员工"
              filterable
              reserve-keyword
              default-first-option
              clearable
              size="small"
              @change="onEmployeeChange"
            >
              <el-option
                v-for="item in employeeOptions"
                :key="item.id"
                :label="employeeLabel(item)"
                :value="String(item.id)"
              />
            </el-select>
            <el-date-picker
              v-model="selectedMonth"
              type="month"
              value-format="yyyy-MM"
              placeholder="选择月份"
              size="small"
              class="filter-month"
              @change="onMonthChange"
            />
            <el-button class="btn-secondary" size="small" @click="downloadTemplate">下载模板</el-button>
            <el-upload
              action="#"
              :show-file-list="false"
              accept=".xlsx,.xls"
              :http-request="doImport"
            >
              <el-button class="btn-secondary" size="small">导入 Excel</el-button>
            </el-upload>
            <el-button class="btn-secondary" size="small" @click="downloadExportAll">导出全部数据</el-button>
            <el-button class="btn-secondary" size="small" @click="downloadExportCurrentMonth">
              按月导出当前月
            </el-button>
            <el-button
              class="btn-secondary"
              size="small"
              :disabled="!selectedEmployeeId"
              @click="downloadExportCurrentEmployee"
            >
              导出当前员工
            </el-button>
          </div>
        </div>
      </template>

      <div v-loading="loadingCalendar" class="calendar-wrap">
        <el-alert
          v-if="!selectedEmployeeId"
          type="info"
          show-icon
          :closable="false"
          title="请先在顶部选择一位员工，然后再查看或编辑该员工当月出勤日历。"
        />
        <template v-else>
          <div class="calendar-legend">
            <span class="legend-item normal">正常</span>
            <span class="legend-item leave">请假</span>
            <span class="legend-item absent">缺勤</span>
            <span class="legend-item overtime">加班</span>
            <span class="legend-item rest">休息</span>
          </div>
          <el-calendar v-model="calendarDate">
            <template slot="dateCell" slot-scope="{ data }">
              <div
                v-if="data.type === 'current-month'"
                class="att-day-cell"
                @click="openDayEditor(data.day)"
              >
                <div class="att-day-head">
                  <span class="att-day-num">{{ dayOfMonth(data.day) }}</span>
                  <span class="att-day-head-right">
                    <span v-if="dayMeta(data.day).is_holiday" class="att-day-holiday-tag">节假日</span>
                    <span v-if="dayMeta(data.day).is_makeup_workday" class="att-day-makeup-tag">调休</span>
                    <span class="att-day-work-flag" :class="{ rest: !dayMeta(data.day).is_workday }">
                      {{ dayMeta(data.day).is_workday ? '工' : '休' }}
                    </span>
                  </span>
                </div>
                <div class="att-day-status" :class="statusClass(dayStatus(data.day))">
                  {{ dayStatus(data.day) }}
                </div>
              </div>
              <div v-else class="att-day-cell att-day-cell--other-month"></div>
            </template>
          </el-calendar>
        </template>
      </div>
    </el-card>

    <el-dialog title="修改出勤状态" :visible.sync="dialogVisible" width="420px">
      <el-form label-width="92px" size="small">
        <el-form-item label="员工">
          <el-input :value="selectedEmployeeLabel" readonly />
        </el-form-item>
        <el-form-item label="日期">
          <el-input :value="editDate" readonly />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="editStatus" class="status-radio-group">
            <el-radio-button v-for="s in statusOptions" :key="s" :label="s" />
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="editStatus === '请假'" label="请假时长">
          <el-radio-group v-model="leaveInputMode" size="small">
            <el-radio-button label="day">一天（8小时）</el-radio-button>
            <el-radio-button label="manual">手动输入小时</el-radio-button>
          </el-radio-group>
          <el-input-number
            v-if="leaveInputMode === 'manual'"
            v-model="leaveHoursManual"
            :min="0.5"
            :max="8"
            :step="0.5"
            :precision="1"
            style="width: 100%; margin-top: 8px;"
            placeholder="请输入请假小时"
          />
        </el-form-item>
        <el-form-item v-if="editStatus === '加班'" label="加班时长">
          <el-radio-group v-model="overtimeInputMode" size="small">
            <el-radio-button label="day">一天（8小时）</el-radio-button>
            <el-radio-button label="manual">手动输入小时</el-radio-button>
          </el-radio-group>
          <el-input-number
            v-if="overtimeInputMode === 'manual'"
            v-model="overtimeHoursManual"
            :min="0.5"
            :max="8"
            :step="0.5"
            :precision="1"
            style="width: 100%; margin-top: 8px;"
            placeholder="请输入加班小时"
          />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveDayStatus">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import { apiBase } from '../api/client'
import { getAdminToken } from '../utils/auth'
import { formatApiError } from '../utils/apiError'
import { downloadBlobResponse } from '../utils/fileExport'
import { guardUploadFile, formatImportErrors, showImportResult } from '../utils/fileImport'

function currentMonth() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function toMonthDate(monthStr) {
  if (!/^\d{4}-\d{2}$/.test(String(monthStr || ''))) return new Date()
  return new Date(`${monthStr}-01T00:00:00`)
}

const HOLIDAYS_2026 = new Set([
  '2026-01-01',
  '2026-01-02',
  '2026-01-03',
  '2026-02-15',
  '2026-02-16',
  '2026-02-17',
  '2026-02-18',
  '2026-02-19',
  '2026-02-20',
  '2026-02-21',
  '2026-02-22',
  '2026-02-23',
  '2026-04-04',
  '2026-04-05',
  '2026-04-06',
  '2026-05-01',
  '2026-05-02',
  '2026-05-03',
  '2026-05-04',
  '2026-05-05',
  '2026-06-19',
  '2026-06-20',
  '2026-06-21',
  '2026-09-25',
  '2026-09-26',
  '2026-09-27',
  '2026-10-01',
  '2026-10-02',
  '2026-10-03',
  '2026-10-04',
  '2026-10-05',
  '2026-10-06',
  '2026-10-07'
])

const MAKEUP_WORKDAYS_2026 = new Set([
  '2026-05-09'
])

function isWeekendDate(dateStr) {
  const d = new Date(`${dateStr}T12:00:00+08:00`)
  if (Number.isNaN(d.getTime())) return false
  const w = d.getUTCDay()
  return w === 0 || w === 6
}

export default {
  name: 'AttendanceCalendar',
  data() {
    return {
      selectedEmployeeId: '',
      selectedMonth: currentMonth(),
      calendarDate: toMonthDate(currentMonth()),
      employeeOptions: [],
      statusOptions: ['正常', '请假', '缺勤', '加班', '休息'],
      calendarDays: [],
      loadingCalendar: false,
      dialogVisible: false,
      editDate: '',
      editStatus: '正常',
      leaveInputMode: 'day',
      leaveHoursManual: 8,
      overtimeInputMode: 'day',
      overtimeHoursManual: 8,
      saving: false
    }
  },
  computed: {
    selectedEmployeeLabel() {
      const id = String(this.selectedEmployeeId || '')
      const e = (this.employeeOptions || []).find((x) => String(x.id) === id)
      return e ? this.employeeLabel(e) : ''
    }
  },
  watch: {
    calendarDate(v) {
      if (!(v instanceof Date) || Number.isNaN(v.getTime())) return
      const m = `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}`
      if (m !== this.selectedMonth) {
        this.selectedMonth = m
        this.loadCalendar()
      }
    }
  },
  async mounted() {
    await this.loadEmployees()
  },
  methods: {
    employeeLabel(e) {
      const n = String((e && e.name) || '').trim() || '未命名'
      const p = String((e && e.phone) || '').trim()
      return p ? `${n}（${p}）` : n
    },
    authHeaders() {
      const t = getAdminToken()
      return t ? { Authorization: `Bearer ${t}` } : {}
    },
    dayOfMonth(dateStr) {
      return String(dateStr || '').slice(-2).replace(/^0/, '')
    },
    dayStatus(dateStr) {
      const row = (this.calendarDays || []).find((x) => x.date === dateStr)
      const meta = this.dayMeta(dateStr)
      const rawStatus = row && row.status ? row.status : '正常'
      if (!meta.is_workday && (rawStatus === '请假' || rawStatus === '正常')) return '休息'
      if (rawStatus === '请假') return this.formatLeaveStatus(this.dayLeaveHours(dateStr))
      if (rawStatus === '加班') return this.formatOvertimeStatus(this.dayOvertimeHours(dateStr))
      return rawStatus
    },
    dayRawStatus(dateStr) {
      const row = (this.calendarDays || []).find((x) => x.date === dateStr)
      return row && row.status ? row.status : '正常'
    },
    dayMeta(dateStr) {
      const row = (this.calendarDays || []).find((x) => x.date === dateStr)
      const isMakeupWorkday = MAKEUP_WORKDAYS_2026.has(dateStr)
      if (row) {
        return {
          is_workday: row.is_workday != null ? !!row.is_workday : this.calcIsWorkday(dateStr),
          is_holiday: row.is_holiday != null ? !!row.is_holiday : HOLIDAYS_2026.has(dateStr),
          is_makeup_workday: row.is_makeup_workday != null ? !!row.is_makeup_workday : isMakeupWorkday
        }
      }
      const isHoliday = HOLIDAYS_2026.has(dateStr)
      const isWorkday = this.calcIsWorkday(dateStr)
      return { is_workday: isWorkday, is_holiday: isHoliday, is_makeup_workday: isMakeupWorkday }
    },
    calcIsWorkday(dateStr) {
      if (MAKEUP_WORKDAYS_2026.has(dateStr)) return true
      if (HOLIDAYS_2026.has(dateStr)) return false
      return !isWeekendDate(dateStr)
    },
    dayLeaveHours(dateStr) {
      const row = (this.calendarDays || []).find((x) => x.date === dateStr)
      const n = Number(row && row.leave_hours)
      return Number.isFinite(n) && n > 0 ? n : 0
    },
    formatLeaveStatus(leaveHours) {
      const h = Number(leaveHours)
      if (!Number.isFinite(h) || h <= 0 || Math.abs(h - 8) < 0.001) return '请假一天'
      const text = Number.isInteger(h) ? String(h) : String(Math.round(h * 10) / 10)
      return `请假${text}小时`
    },
    dayOvertimeHours(dateStr) {
      const row = (this.calendarDays || []).find((x) => x.date === dateStr)
      const n = Number(row && row.overtime_hours)
      return Number.isFinite(n) && n > 0 ? n : 0
    },
    formatOvertimeStatus(overtimeHours) {
      const h = Number(overtimeHours)
      if (!Number.isFinite(h) || h <= 0 || Math.abs(h - 8) < 0.001) return '加班一天'
      const text = Number.isInteger(h) ? String(h) : String(Math.round(h * 10) / 10)
      return `加班${text}小时`
    },
    statusClass(status) {
      const s = String(status || '')
      if (s === '休息') return 'status-rest'
      if (s.startsWith('请假')) return 'status-leave'
      if (s === '缺勤') return 'status-absent'
      if (s.startsWith('加班')) return 'status-overtime'
      return 'status-normal'
    },
    async loadEmployees() {
      try {
        const resp = await this.$api.get('/admin/attendance-calendar/employees')
        this.employeeOptions = resp && resp.code === 200 && Array.isArray(resp.data) ? resp.data : []
      } catch (_e) {
        this.employeeOptions = []
      }
    },
    async onEmployeeChange() {
      await this.loadCalendar()
    },
    async onMonthChange() {
      this.calendarDate = toMonthDate(this.selectedMonth)
      await this.loadCalendar()
    },
    async loadCalendar() {
      if (!this.selectedEmployeeId) {
        this.calendarDays = []
        return
      }
      this.loadingCalendar = true
      try {
        const resp = await this.$api.get('/admin/attendance-calendar/month', {
          params: {
            employee_id: this.selectedEmployeeId,
            month: this.selectedMonth
          }
        })
        if (resp && resp.code === 200 && resp.data && Array.isArray(resp.data.days)) {
          this.calendarDays = resp.data.days
        } else {
          this.calendarDays = []
        }
      } catch (e) {
        this.calendarDays = []
        this.$message.error(formatApiError(e, '加载出勤日历失败'))
      } finally {
        this.loadingCalendar = false
      }
    },
    openDayEditor(dateStr) {
      if (!this.selectedEmployeeId) {
        this.$message.warning('请先选择员工')
        return
      }
      if (!dateStr || !dateStr.startsWith(this.selectedMonth)) return
      this.editDate = dateStr
      this.editStatus = this.dayRawStatus(dateStr)
      const leaveHours = this.dayLeaveHours(dateStr)
      const overtimeHours = this.dayOvertimeHours(dateStr)
      if (this.editStatus === '请假') {
        if (Math.abs(leaveHours - 8) < 0.001 || leaveHours <= 0) {
          this.leaveInputMode = 'day'
          this.leaveHoursManual = 8
        } else {
          this.leaveInputMode = 'manual'
          this.leaveHoursManual = leaveHours
        }
      } else {
        this.leaveInputMode = 'day'
        this.leaveHoursManual = 8
      }
      if (this.editStatus === '加班') {
        if (Math.abs(overtimeHours - 8) < 0.001 || overtimeHours <= 0) {
          this.overtimeInputMode = 'day'
          this.overtimeHoursManual = 8
        } else {
          this.overtimeInputMode = 'manual'
          this.overtimeHoursManual = overtimeHours
        }
      } else {
        this.overtimeInputMode = 'day'
        this.overtimeHoursManual = 8
      }
      this.dialogVisible = true
    },
    async saveDayStatus() {
      if (!this.selectedEmployeeId || !this.editDate) return
      this.saving = true
      const leaveHours =
        this.editStatus === '请假'
          ? this.leaveInputMode === 'manual'
            ? Number(this.leaveHoursManual) || 0
            : 8
          : 0
      const overtimeHours =
        this.editStatus === '加班'
          ? this.overtimeInputMode === 'manual'
            ? Number(this.overtimeHoursManual) || 0
            : 8
          : 0
      try {
        const resp = await this.$api.put('/admin/attendance-calendar/day', {
          employee_id: this.selectedEmployeeId,
          attendance_date: this.editDate,
          status: this.editStatus,
          leave_hours: leaveHours,
          overtime_hours: overtimeHours
        })
        if (resp && resp.code === 200) {
          this.$message.success(resp.message || '保存成功')
          this.dialogVisible = false
          await this.loadCalendar()
        } else {
          this.$message.error((resp && resp.message) || '保存失败')
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '保存失败'))
      } finally {
        this.saving = false
      }
    },
    async doImport(req) {
      const file = req.file
      if (!file) return
      if (!guardUploadFile(this, file)) {
        if (req.onSuccess) req.onSuccess()
        return
      }
      const fd = new FormData()
      fd.append('file', file)
      try {
        const res = await axios.post(`${apiBase}/admin/attendance-calendar/import`, fd, {
          headers: { ...this.authHeaders(), 'Content-Type': 'multipart/form-data' }
        })
        const data = res && res.data
        if (data && data.code === 200) {
          showImportResult(this, data, { successMessage: '导入完成' })
          await this.loadCalendar()
        } else {
          const errText = data && data.data ? formatImportErrors(data.data) : ''
          if (errText) {
            this.$alert(errText, '导入校验失败', { type: 'error' })
          } else {
            this.$message.error((data && data.message) || '导入失败')
          }
        }
      } catch (e) {
        const respData = e && e.response && e.response.data
        const errText = respData && respData.data ? formatImportErrors(respData.data) : ''
        if (errText || (respData && respData.message)) {
          this.$alert(formatImportErrors(respData && respData.data) || respData.message, '导入校验失败', { type: 'error' })
        } else {
          this.$message.error(formatApiError(e, '导入失败'))
        }
      } finally {
        if (req.onSuccess) req.onSuccess()
      }
    },
    async downloadTemplate() {
      try {
        const res = await axios.get(`${apiBase}/admin/attendance-calendar/template`, {
          responseType: 'blob',
          headers: this.authHeaders()
        })
        downloadBlobResponse(res, '出勤日历导入模板')
      } catch (e) {
        this.$message.error(formatApiError(e, '下载失败'))
      }
    },
    async downloadExportAll() {
      try {
        const res = await axios.get(`${apiBase}/admin/attendance-calendar/export`, {
          responseType: 'blob',
          headers: this.authHeaders()
        })
        downloadBlobResponse(res, '出勤日历_全部员工_全量')
      } catch (e) {
        this.$message.error(formatApiError(e, '导出失败'))
      }
    },
    async downloadExportCurrentEmployee() {
      if (!this.selectedEmployeeId) {
        this.$message.warning('请先选择员工')
        return
      }
      try {
        const res = await axios.get(`${apiBase}/admin/attendance-calendar/export`, {
          params: { employee_id: this.selectedEmployeeId },
          responseType: 'blob',
          headers: this.authHeaders()
        })
        downloadBlobResponse(res, `出勤日历_员工_${this.selectedEmployeeId}_全量`)
      } catch (e) {
        this.$message.error(formatApiError(e, '导出失败'))
      }
    },
    async downloadExportCurrentMonth() {
      try {
        const month = this.selectedMonth || currentMonth()
        const res = await axios.get(`${apiBase}/admin/attendance-calendar/export`, {
          params: { month },
          responseType: 'blob',
          headers: this.authHeaders()
        })
        downloadBlobResponse(res, `出勤日历_全部员工_${month}`)
      } catch (e) {
        this.$message.error(formatApiError(e, '导出失败'))
      }
    },
    async doImport(req) {
      const file = req.file
      if (!file) return
      const fd = new FormData()
      fd.append('file', file)
      try {
        const res = await axios.post(`${apiBase}/admin/attendance-calendar/import`, fd, {
          headers: { ...this.authHeaders(), 'Content-Type': 'multipart/form-data' }
        })
        const data = res && res.data
        if (data && data.code === 200) {
          const d = data.data || {}
          let msg = data.message || '导入完成'
          if (d.fail > 0 && d.errors && d.errors.length) {
            msg += `；部分失败：${d.errors.slice(0, 3).join('；')}`
          }
          this.$message[d.fail > 0 ? 'warning' : 'success'](msg)
          await this.loadCalendar()
        } else {
          this.$message.error((data && data.message) || '导入失败')
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '导入失败'))
      } finally {
        if (req.onSuccess) req.onSuccess()
      }
    }
  }
}
</script>

<style>
.employee-management {
  padding: 24px;
  background: #f5f7fa;
}

.employee-card {
  border-radius: 12px;
  box-shadow: 0 2px 14px 0 rgba(0, 0, 0, 0.06);
  border: none;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1d2129;
}

.attendance-calendar-page .card-header--wrap {
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
}

.attendance-calendar-page .toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex: 1;
}

.attendance-calendar-page .filter-employee {
  width: 220px;
}

.attendance-calendar-page .filter-month {
  width: 140px;
}

.attendance-calendar-page .calendar-wrap {
  min-height: 460px;
}

.attendance-calendar-page .calendar-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.attendance-calendar-page .legend-item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.attendance-calendar-page .legend-item.normal {
  background: #ecf5ff;
  color: #165dff;
}

.attendance-calendar-page .legend-item.leave {
  background: #fff7e8;
  color: #fa8c16;
}

.attendance-calendar-page .legend-item.absent {
  background: #fff1f0;
  color: #f5222d;
}

.attendance-calendar-page .legend-item.overtime {
  background: #f6ffed;
  color: #52c41a;
}

.attendance-calendar-page .legend-item.rest {
  background: #f2f3f5;
  color: #86909c;
}

.attendance-calendar-page .att-day-cell {
  height: 78px;
  border-radius: 8px;
  padding: 6px 8px;
  box-sizing: border-box;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.attendance-calendar-page .att-day-cell:hover {
  background: #f7f8fa;
}

.attendance-calendar-page .att-day-cell--other-month {
  background: transparent;
  cursor: default;
}

.attendance-calendar-page .att-day-cell--other-month:hover {
  background: transparent;
}

.attendance-calendar-page .att-day-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.attendance-calendar-page .att-day-head-right {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.attendance-calendar-page .att-day-num {
  font-size: 13px;
  color: #1d2129;
}

.attendance-calendar-page .att-day-work-flag {
  font-size: 11px;
  color: #165dff;
  background: #ecf5ff;
  border-radius: 999px;
  padding: 1px 6px;
}

.attendance-calendar-page .att-day-work-flag.rest {
  color: #86909c;
  background: #f2f3f5;
}

.attendance-calendar-page .att-day-holiday-tag {
  font-size: 10px;
  line-height: 1;
  color: #f53f3f;
  background: #fff1f0;
  border: 1px solid #ffd8d6;
  border-radius: 999px;
  padding: 2px 5px;
}

.attendance-calendar-page .att-day-makeup-tag {
  font-size: 10px;
  line-height: 1;
  color: #722ed1;
  background: #f9f0ff;
  border: 1px solid #d3adf7;
  border-radius: 999px;
  padding: 2px 5px;
}

.attendance-calendar-page .att-day-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 46px;
  height: 22px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  padding: 0 8px;
}

.attendance-calendar-page .att-day-status.status-normal {
  color: #165dff;
  background: #ecf5ff;
}

.attendance-calendar-page .att-day-status.status-leave {
  color: #fa8c16;
  background: #fff7e8;
}

.attendance-calendar-page .att-day-status.status-absent {
  color: #f5222d;
  background: #fff1f0;
}

.attendance-calendar-page .att-day-status.status-overtime {
  color: #52c41a;
  background: #f6ffed;
}

.attendance-calendar-page .att-day-status.status-rest {
  color: #86909c;
  background: #f2f3f5;
}

.attendance-calendar-page .status-radio-group .el-radio-button {
  margin-right: 8px;
}

.attendance-calendar-page .status-radio-group .el-radio-button:last-child {
  margin-right: 0;
}

.attendance-calendar-page .status-radio-group .el-radio-button .el-radio-button__inner {
  border-radius: 999px !important;
  border: 1px solid #dcdfe6 !important;
  padding: 7px 14px;
  line-height: 1;
  box-shadow: none !important;
  transition: all 0.2s ease;
}

.attendance-calendar-page .status-radio-group .el-radio-button:not(:first-child) .el-radio-button__inner {
  border-left: 1px solid #dcdfe6 !important;
}

.attendance-calendar-page .status-radio-group .el-radio-button__inner:hover {
  color: #165dff;
  border-color: #bcd7ff !important;
}

.attendance-calendar-page
  .status-radio-group
  .el-radio-button__orig-radio:checked
  + .el-radio-button__inner {
  color: #165dff;
  background: #ecf5ff;
  border-color: #bcd7ff !important;
}
</style>
